import React from 'react';
import { useSearchParams } from 'react-router-dom';

import { parseQueryString } from '@toss/utils';

import { acError } from '@looko-corp/acloset-react-kit/src/sentry';

import { webViewEventSchema } from './config/web-view-event-schema';

/**
 * 웹뷰 Load provider
 */
export function WebViewLoadProvider({ children }: { children: React.ReactNode }) {
  const [searchParams, setSearchParams] = useSearchParams();

  const handleMessage = React.useCallback(
    (e: Event) => {
      if (e instanceof MessageEvent) {
        try {
          if (typeof e.data !== 'string') {
            throw new Error('MessageEvent data가 string이 아닙니다.');
          }

          const event = webViewEventSchema.safeParse(JSON.parse(e.data));

          if (event.error) {
            throw new Error('정의되지 않은 이벤트 타입');
          }

          if (event.data.type === 'SET_SEARCHPARAMS') {
            const query = {
              ...parseQueryString(searchParams.toString()),
              ...event.data.data,
            } as object;
            const newQuery: Record<string, string> = {};
            Object.entries(query).forEach(([key, value]) => {
              if (typeof value === 'object') {
                newQuery[key] = JSON.stringify(value);
                return;
              }
              newQuery[key] = value;
            });

            setSearchParams(newQuery);
            return;
          }
        } catch (err) {
          acError(`WebView init 실패 ${err}`, err);
        }
      }
    },
    [searchParams, setSearchParams],
  );

  React.useEffect(() => {
    if (window.ReactNativeWebView == null) {
      return;
    }

    window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'ONLOAD' }));

    document.addEventListener('message', handleMessage);
    window.addEventListener('message', handleMessage);

    return () => {
      document.removeEventListener('message', handleMessage);
      window.removeEventListener('message', handleMessage);
    };
  }, [handleMessage]);

  return children;
}
